import { type AppPreloadQuery } from '#app/__generated__/relay/AppPreloadQuery.graphql.js';
import { type AppConfiguration } from '#app/types.js';
import { type PageContext } from '../src/contexts/PageContextContext/PageContextContext.js';
import { AppClient } from '../src/features/app/components/AppClient.js';
import { Environment, type PreloadedQuery } from '@contra/relay';
import { type AnalyticsBrowser } from '@segment/analytics-next';
import { useEffect, useId, useState, useTransition } from 'react';
import { resolveRoute } from 'vike/routing';

type PageTransitionProps = {
  readonly analytics: AnalyticsBrowser;
  readonly appConfiguration: AppConfiguration;
  readonly pageContext: PageContext;
  readonly pageRef: JSX.Element | undefined;
  readonly preloadedQuery: PreloadedQuery<AppPreloadQuery>;
  readonly relayEnvironment: InstanceType<typeof Environment>;
};

/**
 * @package
 */
export const PageTransition = ({
  analytics,
  appConfiguration,
  pageContext,
  pageRef,
  preloadedQuery,
  relayEnvironment,
}: PageTransitionProps) => {
  const [CurrentPage, setCurrentPage] = useState(pageRef);
  const [currentPageContext, setCurrentPageContext] =
    useState<PageContext>(pageContext);
  const id = useId();

  const [isTransitioning, startTransition] = useTransition();

  useEffect(() => {
    startTransition(() => {
      setCurrentPage(pageRef);
      setCurrentPageContext(pageContext);
    });
  }, [pageContext, pageRef]);

  const { match: isEmbeddablePage } = resolveRoute(
    '/request-guide/embed',
    pageContext.urlPathname,
  );

  const { match: isMessagesPage } = resolveRoute(
    '/messages',
    pageContext.urlPathname,
  );

  return (
    <AppClient
      analytics={analytics}
      appConfiguration={appConfiguration}
      pageContext={currentPageContext}
      preloadedQuery={preloadedQuery}
      relayEnvironment={relayEnvironment}
      sentryEnabled={Boolean(appConfiguration.sentry)}
    >
      {isEmbeddablePage || isMessagesPage ? null : (
        <div
          className={
            isTransitioning
              ? 'page-transition-animation__continue'
              : 'page-transition-animation__finished'
          }
          id="page-transition-animation"
          key={id}
        />
      )}
      {CurrentPage ? CurrentPage : null}
    </AppClient>
  );
};
